import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { getRoleAccessStatus, RoleAccessStatus } from '~/utils/roleAccess';
import log from '~/utils/log';
import { prefixLocalePath } from '~/utils/prefixLocalePath';

const refreshAttemptStateKey = 'academy-access:refresh-attempted';

/**
 * Ensures hardcoded Academy pages follow the section's role-based access rules before Docebo session setup.
 */
export default defineNuxtRouteMiddleware(async (to) => {
    const auth = useAuth();
    const authValue = auth.value;

    const { data: config, error: configError } = await useConfig(to);
    if (configError.value || !config.value?.content) {
        log.error('[academy-access] Failed to load config', configError.value);
        throw new Error('Failed to load configuration.');
    }

    const status = getRoleAccessStatus(config.value.content.rolesWithAccess, authValue);
    if (status === RoleAccessStatus.NeedsLogin) {
        const { redirectToLogin } = useRedirectToLogin();
        log.debug('[academy-access] Redirecting to login.');
        return redirectToLogin({ to, refreshAttemptStateKey });
    }

    if (status !== RoleAccessStatus.Forbidden) return;

    const fallbackStory = config.value?.content.lockedPagesFallbackStory;
    const fallbackUrl = hasStoryblokLinkTarget(fallbackStory) ? useStoryblokUrl(fallbackStory) : '';
    if (fallbackUrl) {
        log.debug(`[academy-access] Redirecting to locked pages fallback: ${fallbackUrl}`);
        return navigateTo(fallbackUrl, { replace: true });
    }

    const sectionRootUrl = getSectionRootUrl(to);
    if (isSamePath(sectionRootUrl, to.path)) return;

    log.warn(`[academy-access] Missing locked pages fallback, redirecting to section root: ${sectionRootUrl}`);
    return navigateTo(sectionRootUrl, { replace: true });
});

const hasStoryblokLinkTarget = (link?: ConfigStoryblok['lockedPagesFallbackStory']) =>
    Boolean(link?.id || link?.cached_url || link?.url);

const isSamePath = (target: string, currentPath: string) => {
    const normalize = (path: string) => `/${path.replace(/^\/+|\/+$/g, '')}`;

    return normalize(target) === normalize(currentPath);
};

function getSectionRootUrl(to: RouteLocationNormalizedGeneric) {
    const { urlSegment } = useLanguage(to);
    const sectionRootPath = useCurrentSectionConfig(to).value?.sectionRootPath ?? '/';

    return prefixLocalePath(sectionRootPath, urlSegment.value);
}
